import React, { useEffect, useContext, useState, useMemo } from "react";
import { useHistory, useParams } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import axios from "axios";
import Select from "react-select";
import makeAnimated from "react-select/animated";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";

// Base URL & functions
import { BASE_URL } from "../../../../../BaseUrl";
import {
  _t,
  getCookie,
  customStyle,
  tableLoading,
  currencySymbolLeft,
  currencySymbolRight,
} from "../../../../../functions/Functions";
import { ToastSuccess, ToastError } from "../../../../../functions/ToastShow";
import { useCustomHooks } from "../../../../../functions/Hooks";

// Contexts
import { RestaurantContext } from "../../../../../contexts/Restaurant";

// Components
import ManageSidebar from "../ManageSidebar";

const PeelingProcessAction = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const { roundOffStock, qtyAmountPoints } = useCustomHooks();

  const {
    loading,
    setLoading,
    dataPaginating,
    branchForSearch,
    ingredientItemForSearch,
    getIngredientItem,
  } = useContext(RestaurantContext);
  const [submitting, setSubmitting] = useState(false);
  const [costLoading, setCostLoading] = useState(false);

  // Main state
  const [peeling, setPeeling] = useState({
    document_identity: "",
    document_no: "",
    date: new Date(),
    branch: null,
    item: null,
    base_unit: "",
    item_units: [],
    unit: null,
    conversion_qty: 1,
    cost_price: "",
    before_peeling: "",
    after_peeling: "",
    wastage: "",
    remarks: "",
  });

  // Pre-select first branch in add mode
  useEffect(() => {
    if (branchForSearch && branchForSearch.length > 0 && !peeling.branch && !isEdit) {
      setPeeling((prev) => ({ ...prev, branch: branchForSearch[0] }));
    }
  }, [branchForSearch, isEdit]);

  // Load ingredients if not loaded
  useEffect(() => {
    if (!ingredientItemForSearch) {
      getIngredientItem();
    }
  }, []);

  // Fetch unit conversions for selected item
  const handleGetUnits = async (item) => {
    if (!item?.id) return null;
    const url = BASE_URL + "/settings/get-unit_item/" + item.id;
    try {
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${getCookie()}` },
      });
      if (res.data) {
        return res.data;
      }
    } catch (error) {
      console.log("check unit error:", error);
    }
    return null;
  };

  // Fetch average rate / unit cost for selected item, unit, and branch with qty = 1
  const fetchItemAvgRate = async (item, unit, branch) => {
    const branchId = branch?.id || peeling.branch?.id;
    if (!item?.id || !unit?.id || !branchId) return null;

    const url = BASE_URL + "/settings/get-qty-avgrate";
    let formData = new FormData();
    formData.append("branch_id", branchId);
    formData.append("item_id", item.id);
    formData.append("unit_id", unit.id);
    formData.append("qty", 1);

    try {
      setCostLoading(true);
      const res = await axios.post(url, formData, {
        headers: { Authorization: `Bearer ${getCookie()}` },
      });
      if (res.data && res.data.avg_rate !== undefined && res.data.avg_rate !== null) {
        const rate = parseFloat(res.data.avg_rate);
        if (!isNaN(rate)) {
          if (rate > 0) {
            return roundOffStock(rate);
          }
          // If avg_rate from stock ledger is 0, fallback to master item cost price if present
          if (item?.cost_price && parseFloat(item.cost_price) > 0) {
            return roundOffStock(parseFloat(item.cost_price));
          }
          return roundOffStock(rate);
        }
      }
    } catch (error) {
      console.log("Error fetching avg rate:", error);
    } finally {
      setCostLoading(false);
    }
    return null;
  };

  // On item select: fetch units, set default unit, conversion_qty, base_unit, and cost_price
  const handleSelectItem = async (selectedItem) => {
    if (!selectedItem) {
      setPeeling((prev) => ({
        ...prev,
        item: null,
        base_unit: "",
        item_units: [],
        unit: null,
        conversion_qty: 1,
        cost_price: "",
      }));
      return;
    }

    const itemCost =
      selectedItem.cost_price !== undefined && selectedItem.cost_price !== null
        ? selectedItem.cost_price
        : "";

    setPeeling((prev) => ({
      ...prev,
      item: selectedItem,
      base_unit: selectedItem.unit_id || "",
      item_units: [],
      unit: null,
      conversion_qty: 1,
      cost_price: itemCost,
    }));

    const units = await handleGetUnits(selectedItem);
    if (units && units.length > 0) {
      const defaultUnit = units[1] ?? units[0];
      const convQty = defaultUnit?.qty
        ? Number(roundOffStock(defaultUnit.qty)) || 1
        : 1;

      // Fetch avg rate for default unit with qty: 1
      const avgRate = await fetchItemAvgRate(
        selectedItem,
        defaultUnit,
        peeling.branch
      );
      const finalCost =
        avgRate !== null && avgRate !== undefined && avgRate !== ""
          ? avgRate
          : itemCost;

      setPeeling((prev) => {
        if (prev.item?.id === selectedItem.id) {
          return {
            ...prev,
            item: selectedItem,
            base_unit: selectedItem.unit_id || "",
            item_units: units,
            unit: defaultUnit,
            conversion_qty: convQty,
            cost_price: finalCost,
          };
        }
        return prev;
      });
    }
  };

  // On unit select: update unit, conversion_qty, and fetch avg rate
  const handleSelectUnit = async (selectedUnit) => {
    if (!selectedUnit) return;
    const convQty = selectedUnit?.qty
      ? Number(roundOffStock(selectedUnit.qty)) || 1
      : 1;

    setPeeling((prev) => ({
      ...prev,
      unit: selectedUnit,
      conversion_qty: convQty,
    }));

    if (peeling.item) {
      const avgRate = await fetchItemAvgRate(
        peeling.item,
        selectedUnit,
        peeling.branch
      );
      if (avgRate !== null && avgRate !== undefined) {
        setPeeling((prev) => {
          if (prev.unit?.id === selectedUnit.id) {
            return {
              ...prev,
              cost_price: avgRate,
            };
          }
          return prev;
        });
      }
    }
  };

  // Unified quantity input handler with automatic sync
  const handleQtyChange = (field, val) => {
    setPeeling((prev) => {
      const updated = { ...prev, [field]: val };
      const b = parseFloat(field === "before_peeling" ? val : prev.before_peeling) || 0;

      if (field === "before_peeling") {
        const a = parseFloat(prev.after_peeling);
        if (!isNaN(a) && a > b && b > 0) updated.after_peeling = val;
        updated.wastage = prev.after_peeling !== "" && b >= 0 ? roundOffStock(Math.max(0, b - (parseFloat(updated.after_peeling) || 0))) : "";
      } else if (field === "after_peeling") {
        const a = parseFloat(val);
        if (b > 0 && !isNaN(a) && a > b) {
          ToastError({ title: _t(t("After Peeling cannot be greater than Before Peeling")), toastId: "after-peeling-greater" });
          return prev;
        }
        updated.wastage = val !== "" && b > 0 ? roundOffStock(Math.max(0, b - (a || 0))) : "";
      } else if (field === "wastage") {
        const w = parseFloat(val);
        if (b > 0 && !isNaN(w) && w > b) {
          ToastError({ title: _t(t("Wastage cannot be greater than Before Peeling")), toastId: "wastage-greater" });
          return prev;
        }
        if (val !== "" && b > 0) updated.after_peeling = roundOffStock(Math.max(0, b - (w || 0)));
      }
      return updated;
    });
  };


  // Single calculation memo for all metrics (computed only once)
  // Standard Yield Costing: Cost per Peeled Unit = Total Raw Cost / Peeled Weight
  const calculations = useMemo(() => {
    const before = parseFloat(peeling.before_peeling) || 0;
    const after = parseFloat(peeling.after_peeling) || 0;
    const wastage = parseFloat(peeling.wastage) || (before > 0 && after >= 0 && peeling.after_peeling !== "" ? Math.max(0, before - after) : 0);
    const rawCost = parseFloat(peeling.cost_price) || 0;

    const wastagePercent = before > 0 ? Number(((wastage / before) * 100).toFixed(2)) : 0;
    const yieldPercent = before > 0 ? Number(((after / before) * 100).toFixed(2)) : 0;
    const totalRawCost = Number((before * rawCost).toFixed(2));
    const wastageCost = Number((wastage * rawCost).toFixed(2));
    const costPerPeeled = after > 0 && totalRawCost > 0 ? Number((totalRawCost / after).toFixed(2)) : 0;

    return {
      before,
      after,
      wastage: roundOffStock(wastage),
      rawCost,
      wastagePercent,
      yieldPercent,
      totalRawCost,
      wastageCost,
      costPerPeeled,
    };
  }, [peeling.before_peeling, peeling.after_peeling, peeling.wastage, peeling.cost_price, roundOffStock]);

  // Load existing data in edit mode
  useEffect(() => {
    if (isEdit) {
      const fetchPeeling = async () => {
        setLoading(true);
        try {
          const url = BASE_URL + "/settings/peeling/" + id;
          const res = await axios.get(url, {
            headers: { Authorization: `Bearer ${getCookie()}` },
          });
          const data =
            res.data?.data ||
            (Array.isArray(res.data) ? res.data[0] : res.data);

          if (data) {
            const loadedItem = data.item || {
              id: data.item_id,
              name: data.item_name || "",
              cost_price: data.cost_price ?? data.cost ?? "",
            };

            const unitsList =
              (await handleGetUnits({ id: data.item_id })) || [];

            const loadedCost =
              data.cost_price ??
              data.cost ??
              data.raw_cost ??
              (loadedItem?.cost_price ?? "");

            setPeeling({
              document_identity:
                data.document_identity ||
                (data.document_no ? `PL-${data.document_no}` : ""),
              document_no: data.document_no || "",
              date: data.document_date
                ? new Date(data.document_date)
                : new Date(),
              branch: data.branch || (data.branch_id ? { id: data.branch_id } : null),
              item: loadedItem,
              base_unit: data.base_unit || "",
              item_units: unitsList,
              unit: data.unit || (data.unit_id ? { id: data.unit_id } : null),
              conversion_qty: Number(data.conversion_qty) || 1,
              cost_price: loadedCost !== null && loadedCost !== undefined ? loadedCost : "",
              before_peeling: data.before_peeling_weight ?? (data.qty ?? ""),
              after_peeling: data.after_peeling_weight ?? "",
              wastage: data.wastage_qty ?? "",
              remarks: data.remarks || "",
            });
          }
        } catch (error) {
          console.log("Error loading peeling details:", error);
          ToastError({
            title: _t(t("Failed to load peeling details")),
          });
        } finally {
          setLoading(false);
        }
      };

      fetchPeeling();
    }
  }, [id]);

  // Submit Handler - uses memoized calculations directly
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!peeling.branch) {
      ToastError({ title: _t(t("Please select branch")) });
      return;
    }
    if (!peeling.item) {
      ToastError({ title: _t(t("Please select an ingredient item")) });
      return;
    }
    if (!peeling.unit) {
      ToastError({ title: _t(t("Please select a unit")) });
      return;
    }

    const beforeWeight = Number(peeling.before_peeling) || 0;
    const afterWeight = Number(peeling.after_peeling) || 0;
    const convQty = Number(peeling.conversion_qty) || 1;
    const baseQty = Number(roundOffStock(beforeWeight * convQty)) || beforeWeight;

    if (beforeWeight <= 0) {
      ToastError({
        title: _t(t("Before Peeling quantity must be greater than zero")),
      });
      return;
    }
    if (peeling.after_peeling === "" || afterWeight < 0) {
      ToastError({ title: _t(t("After Peeling quantity is required")) });
      return;
    }
    if (afterWeight > beforeWeight) {
      ToastError({
        title: _t(t("After Peeling cannot be greater than Before Peeling")),
        toastId: "after-peeling-greater",
      });
      return;
    }

    const payload = {
      branch_id: Number(peeling.branch?.id),
      document_date: moment(peeling.date).format("YYYY-MM-DD"),
      item_id: peeling.item?.id,
      item_name: peeling.item?.name,
      base_qty: baseQty,
      conversion_qty: convQty,
      qty: beforeWeight,
      unit_id: peeling.unit?.id,
      base_unit: peeling.base_unit || peeling.item?.unit_id,
      before_peeling_weight: beforeWeight,
      after_peeling_weight: afterWeight,
      wastage_qty: Number(calculations.wastage) || 0,
      cost_price: calculations.rawCost,
      cost_per_peeled: calculations.costPerPeeled,
      wastage_percent: calculations.wastagePercent,
      total_cost: calculations.totalRawCost,
      wastage_cost: calculations.wastageCost,
      remarks: peeling.remarks || "",
    };

    setSubmitting(true);
    try {
      const url = isEdit
        ? `${BASE_URL}/settings/peeling/${id}`
        : `${BASE_URL}/settings/peeling`;

      const method = isEdit ? "put" : "post";
      await axios[method](url, payload, {
        headers: { Authorization: `Bearer ${getCookie()}` },
      });

      ToastSuccess({
        title: _t(
          t(
            isEdit
              ? "Peeling process has been updated successfully"
              : "Peeling process has been created successfully"
          )
        ),
      });

      history.push("/dashboard/manage/stock/peeling-process");
    } catch (error) {
      console.log("Submit error:", error);
      const errMsg =
        error.response?.data?.message ||
        error.response?.data?.error ||
        _t(t("Please try again later"));
      ToastError({ title: errMsg });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>
          {_t(t(isEdit ? "Edit Peeling Process" : "Add Peeling Process"))}
        </title>
      </Helmet>

      {/* main body */}
      <main id="main" data-simplebar>
        <div className="container-fluid">
          <div className="row t-mt-10 gx-2">
            {/* left Sidebar */}
            <div className="col-lg-2 col-xxl-2 t-mb-30 mb-lg-0">
              <ManageSidebar />
            </div>
            {/* left Sidebar ends */}

            {/* Rightbar contents */}
            <div className="col-lg-10 col-xxl-10 t-mb-30 mb-lg-0">
              <div className="t-bg-white">
                <div
                  className="fk-scroll--pos-menu table-bottom-info-hide"
                  data-simplebar
                >
                  <div className="t-pl-15 t-pr-15">
                    {/* next page data spin loading */}
                    <div className={`${loading && "loading"}`}></div>
                    {/* spin loading ends */}

                    {/* Loading effect */}
                    {dataPaginating === true ? (
                      tableLoading()
                    ) : (
                      <div key="peeling-form">
                        <div className="row gx-2 align-items-center t-pt-15 t-pb-15">
                          <div className="col-md-6 col-lg-5 t-mb-15 mb-md-0">
                            <ul className="t-list fk-breadcrumb">
                              <li className="fk-breadcrumb__list">
                                <span className="t-link fk-breadcrumb__link text-capitalize">
                                  {_t(
                                    t(
                                      isEdit
                                        ? "Edit Peeling Process"
                                        : "Add Peeling Process"
                                    )
                                  )}
                                </span>
                              </li>
                            </ul>
                          </div>
                          <div className="col-md-6 col-lg-7">
                            <div className="row gx-3 align-items-center"></div>
                          </div>
                        </div>

                        {/* Form starts here */}
                        <form
                          className="row card p-2 mx-3 mb-5 table-text peeling-process-form"
                          onSubmit={handleSubmit}
                          autoComplete="off"
                        >
                          <div className="col-12">
                            <div className="row">
                              {/* Document No */}
                              <div className="form-group col-12 col-md-3 mt-2">
                                <div
                                  className="d-flex justify-content-between align-items-center mb-1"
                                  style={{ minHeight: "20px" }}
                                >
                                  <label
                                    htmlFor="document_no"
                                    className="control-label mb-0"
                                  >
                                    {_t(t("Document No"))}
                                  </label>
                                </div>
                                <div className="mb-2">
                                  <input
                                    disabled={true}
                                    type="text"
                                    className="form-control table-text"
                                    id="document_no"
                                    name="document_no"
                                    value={
                                      peeling.document_identity ||
                                      (isEdit ? "" : "Auto No")
                                    }
                                    placeholder="Auto No"
                                  />
                                </div>
                              </div>

                              {/* Date */}
                              <div className="form-group col-12 col-md-3 mt-2">
                                <div
                                  className="d-flex justify-content-between align-items-center mb-1"
                                  style={{ minHeight: "20px" }}
                                >
                                  <label
                                    htmlFor="peelingDate"
                                    className="control-label mb-0"
                                  >
                                    {_t(t("Date"))}
                                    <span className="text-danger">*</span>
                                  </label>
                                </div>
                                <div className="mb-2">
                                  <DatePicker
                                    selected={peeling.date}
                                    onChange={(date) =>
                                      setPeeling({ ...peeling, date })
                                    }
                                    peekNextMonth
                                    showMonthDropdown
                                    showYearDropdown
                                    dropdownMode="select"
                                    className="form-control table-text"
                                    wrapperClassName="d-block w-100"
                                    placeholderText={_t(t("Select date"))}
                                    dateFormat="dd-MM-yyyy"
                                    shouldCloseOnSelect={false}
                                  />
                                </div>
                              </div>

                              {/* Branch */}
                              <div className="form-group col-12 col-md-3 mt-2">
                                <div
                                  className="d-flex justify-content-between align-items-center mb-1"
                                  style={{ minHeight: "20px" }}
                                >
                                  <label
                                    htmlFor="branch"
                                    className="control-label text-truncate mb-0"
                                  >
                                    {_t(t("Branch"))}
                                    <span className="text-danger">*</span>
                                  </label>
                                </div>
                                <div className="mb-2">
                                  <Select
                                    options={branchForSearch || []}
                                    components={makeAnimated()}
                                    getOptionLabel={(option) => option.name}
                                    getOptionValue={(option) => option.id}
                                    value={peeling.branch}
                                    classNamePrefix="select"
                                    onChange={(b) => {
                                      setPeeling((prev) => ({ ...prev, branch: b }));
                                      if (peeling.item && peeling.unit && b?.id) {
                                        fetchItemAvgRate(peeling.item, peeling.unit, b).then((rate) => {
                                          if (rate !== null && rate !== undefined) {
                                            setPeeling((prev) => ({ ...prev, cost_price: rate }));
                                          }
                                        });
                                      }
                                    }}
                                    maxMenuHeight="200px"
                                    styles={customStyle}
                                    placeholder={
                                      _t(t("Please select branch")) + ".."
                                    }
                                  />
                                </div>
                              </div>

                              {/* Raw Item / Ingredient */}
                              <div className="form-group col-12 col-md-3 mt-2">
                                <div
                                  className="d-flex justify-content-between align-items-center mb-1"
                                  style={{ minHeight: "20px" }}
                                >
                                  <label
                                    htmlFor="item"
                                    className="control-label text-truncate mb-0"
                                  >
                                    {_t(t("Raw Item / Ingredient"))}
                                    <span className="text-danger">*</span>
                                  </label>
                                </div>
                                <div className="mb-2">
                                  <Select
                                    options={
                                      ingredientItemForSearch !== null
                                        ? ingredientItemForSearch
                                        : []
                                    }
                                    components={makeAnimated()}
                                    getOptionLabel={(option) => option.name}
                                    getOptionValue={(option) => option.id}
                                    value={peeling.item}
                                    classNamePrefix="select"
                                    onChange={handleSelectItem}
                                    maxMenuHeight="200px"
                                    styles={customStyle}
                                    placeholder={
                                      _t(t("Select Raw / Ingredient Item")) + ".."
                                    }
                                    isClearable
                                  />
                                </div>
                              </div>

                              {/* Unit */}
                              <div className="form-group col-12 col-md-3 mt-2">
                                <div
                                  className="d-flex justify-content-between align-items-center mb-1"
                                  style={{ minHeight: "20px" }}
                                >
                                  <label
                                    htmlFor="unit"
                                    className="control-label text-truncate mb-0"
                                  >
                                    {_t(t("Unit"))}
                                    <span className="text-danger">*</span>
                                  </label>
                                  {peeling.unit && (
                                    <small className="text-muted table-text">
                                      1 {peeling.unit.name || "Unit"} ={" "}
                                      {peeling.conversion_qty || 1}
                                      {peeling.item_units &&
                                      peeling.item_units[0]?.name &&
                                      peeling.item_units[0].name !== peeling.unit.name
                                        ? ` ${peeling.item_units[0].name}`
                                        : ""}
                                    </small>
                                  )}
                                </div>
                                <div className="mb-2">
                                  <Select
                                    options={
                                      peeling.item_units
                                        ? peeling.item_units
                                        : []
                                    }
                                    components={makeAnimated()}
                                    getOptionLabel={(option) => option.name}
                                    getOptionValue={(option) => option.id}
                                    value={peeling.unit}
                                    classNamePrefix="select"
                                    onChange={handleSelectUnit}
                                    maxMenuHeight="200px"
                                    styles={customStyle}
                                    isDisabled={
                                      !peeling.item_units ||
                                      peeling.item_units.length === 0
                                    }
                                    placeholder={
                                      peeling.item
                                        ? _t(t("Select unit")) + ".."
                                        : _t(t("Select an item first")) + ".."
                                    }
                                  />
                                </div>
                              </div>

                              {/* Cost Price */}
                              <div className="form-group col-12 col-md-3 mt-2">
                                <div
                                  className="d-flex justify-content-between align-items-center mb-1"
                                  style={{ minHeight: "20px" }}
                                >
                                  <label
                                    htmlFor="cost_price"
                                    className="control-label text-truncate mb-0"
                                  >
                                    {_t(t("Cost Price"))}{" "}
                                    {peeling.unit?.name
                                      ? `(${_t(t("per"))} ${peeling.unit.name})`
                                      : ""}
                                  </label>
                                  {costLoading && (
                                    <small className="text-muted">
                                      <i className="fa fa-spinner fa-spin mr-1"></i>
                                      {_t(t("Fetching avg rate..."))}
                                    </small>
                                  )}
                                </div>
                                <div className="mb-2">
                                  <div className="input-group">
                                    <input
                                      type="number"
                                      step="any"
                                      min="0"
                                      className="form-control table-text text-left"
                                      id="cost_price"
                                      name="cost_price"
                                      placeholder="0.00"
                                      value={peeling.cost_price ?? ""}
                                      onChange={(e) => {
                                        const value = e.target.value;
                                        setPeeling((prev) => ({
                                          ...prev,
                                          cost_price: value,
                                        }));
                                      }}
                                      onKeyDown={(evt) =>
                                        [
                                          "e",
                                          "E",
                                          "+",
                                          "-",
                                          "ArrowUp",
                                          "ArrowDown",
                                        ].includes(evt.key) &&
                                        evt.preventDefault()
                                      }
                                      onWheel={(e) => e.target.blur()}
                                    />
                                    {peeling.unit?.name && (
                                      <div className="input-group-append">
                                        <span className="input-group-text table-text">
                                          /{peeling.unit.name}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>

                              {/* Before Peeling */}
                              <div className="form-group col-12 col-md-3 mt-2">
                                <div
                                  className="d-flex justify-content-between align-items-center mb-1"
                                  style={{ minHeight: "20px" }}
                                >
                                  <label
                                    htmlFor="before_peeling"
                                    className="control-label mb-0"
                                  >
                                    {_t(t("Before Peeling"))}
                                    <span className="text-danger">*</span>
                                  </label>
                                </div>
                                <div className="mb-2">
                                  <div className="input-group">
                                    <input
                                      type="number"
                                      step={
                                        qtyAmountPoints
                                          ? qtyAmountPoints()
                                          : "any"
                                      }
                                      min="0"
                                      className="form-control table-text text-left"
                                      id="before_peeling"
                                      name="before_peeling"
                                      placeholder="0.00"
                                      value={peeling.before_peeling ?? ""}
                                      onChange={(e) =>
                                        handleQtyChange(
                                          "before_peeling",
                                          e.target.value
                                        )
                                      }
                                      onKeyDown={(evt) =>
                                        [
                                          "e",
                                          "E",
                                          "+",
                                          "-",
                                          "ArrowUp",
                                          "ArrowDown",
                                        ].includes(evt.key) &&
                                        evt.preventDefault()
                                      }
                                      onWheel={(e) => e.target.blur()}
                                      required
                                    />
                                    {peeling.unit?.name && (
                                      <div className="input-group-append">
                                        <span className="input-group-text table-text">
                                          {peeling.unit.name}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>

                              {/* After Peeling */}
                              <div className="form-group col-12 col-md-3 mt-2">
                                <div
                                  className="d-flex justify-content-between align-items-center mb-1"
                                  style={{ minHeight: "20px" }}
                                >
                                  <label
                                    htmlFor="after_peeling"
                                    className="control-label mb-0"
                                  >
                                    {_t(t("After Peeling"))}
                                    <span className="text-danger">*</span>
                                  </label>
                                </div>
                                <div className="mb-2">
                                  <div className="input-group">
                                    <input
                                      type="number"
                                      step={
                                        qtyAmountPoints
                                          ? qtyAmountPoints()
                                          : "any"
                                      }
                                      min="0"
                                      max={peeling.before_peeling || undefined}
                                      className="form-control table-text text-left"
                                      id="after_peeling"
                                      name="after_peeling"
                                      placeholder="0.00"
                                      value={peeling.after_peeling ?? ""}
                                      onChange={(e) =>
                                        handleQtyChange(
                                          "after_peeling",
                                          e.target.value
                                        )
                                      }
                                      onKeyDown={(evt) =>
                                        [
                                          "e",
                                          "E",
                                          "+",
                                          "-",
                                          "ArrowUp",
                                          "ArrowDown",
                                        ].includes(evt.key) &&
                                        evt.preventDefault()
                                      }
                                      onWheel={(e) => e.target.blur()}
                                      required
                                    />
                                    {peeling.unit?.name && (
                                      <div className="input-group-append">
                                        <span className="input-group-text table-text">
                                          {peeling.unit.name}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>

                              {/* Wastage */}
                              <div className="form-group col-12 col-md-3 mt-2">
                                <div
                                  className="d-flex justify-content-between align-items-center mb-1"
                                  style={{ minHeight: "20px" }}
                                >
                                  <label
                                    htmlFor="wastage"
                                    className="control-label mb-0"
                                  >
                                    {_t(t("Wastage"))}
                                  </label>
                                  <span
                                    className="badge font-weight-bold"
                                    style={{
                                      fontSize: "0.8rem",
                                      padding: "2px 7px",
                                      backgroundColor:
                                        calculations.wastagePercent > 0
                                          ? "#fee2e2"
                                          : "#f1f5f9",
                                      color:
                                        calculations.wastagePercent > 0
                                          ? "#dc2626"
                                          : "#64748b",
                                      border: `1px solid ${
                                        calculations.wastagePercent > 0
                                          ? "#fca5a5"
                                          : "#e2e8f0"
                                      }`,
                                      borderRadius: "4px",
                                    }}
                                  >
                                    {calculations.wastagePercent || 0}%
                                  </span>
                                </div>
                                <div className="mb-2">
                                  <div className="input-group">
                                    <input
                                      type="number"
                                      step={
                                        qtyAmountPoints
                                          ? qtyAmountPoints()
                                          : "any"
                                      }
                                      min="0"
                                      className="form-control table-text text-left"
                                      id="wastage"
                                      name="wastage"
                                      placeholder="0.00"
                                      value={peeling.wastage ?? ""}
                                      onChange={(e) =>
                                        handleQtyChange(
                                          "wastage",
                                          e.target.value
                                        )
                                      }
                                      onKeyDown={(evt) =>
                                        [
                                          "e",
                                          "E",
                                          "+",
                                          "-",
                                          "ArrowUp",
                                          "ArrowDown",
                                        ].includes(evt.key) &&
                                        evt.preventDefault()
                                      }
                                      onWheel={(e) => e.target.blur()}
                                    />
                                    {peeling.unit?.name && (
                                      <div className="input-group-append">
                                        <span className="input-group-text table-text">
                                          {peeling.unit.name}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>

                              {/* Remarks */}
                              <div className="form-group col-12 col-md-9 mt-2">
                                <div
                                  className="d-flex justify-content-between align-items-center mb-1"
                                  style={{ minHeight: "20px" }}
                                >
                                  <label
                                    htmlFor="remarks"
                                    className="control-label mb-0"
                                  >
                                    {_t(t("Remarks"))}
                                  </label>
                                </div>
                                <div className="mb-2">
                                  <input
                                    type="text"
                                    className="form-control table-text text-left"
                                    id="remarks"
                                    name="remarks"
                                    placeholder={_t(t("Remarks")) + "..."}
                                    value={peeling.remarks || ""}
                                    onChange={(e) => {
                                      const value = e.target.value;
                                      setPeeling((prev) => ({
                                        ...prev,
                                        remarks: value,
                                      }));
                                    }}
                                  />
                                </div>
                              </div>

                              {/* Cost & Yield Summary (Optimized & Compact) */}
                              <div className="col-12 mt-3 pt-3 border-top">
                                <div className="row g-2">
                                  {/* Highlight Card: Cost Per Peeled Unit */}
                                  <div className="col-12 col-sm-6 col-lg-3 mb-2 mb-lg-0">
                                    <div
                                      className="rounded h-100 d-flex flex-column justify-content-between"
                                      style={{
                                        padding: "10px 14px",
                                        backgroundColor: "#f0fdf4",
                                        border: "1px solid #86efac",
                                        borderRadius: "6px",
                                        minHeight: "72px",
                                      }}
                                    >
                                      <div className="d-flex justify-content-between align-items-center">
                                        <span
                                          className="text-uppercase font-weight-bold"
                                          style={{
                                            fontSize: "0.72rem",
                                            color: "#166534",
                                            letterSpacing: "0.4px",
                                          }}
                                        >
                                          {_t(t("Cost per"))}{" "}
                                          {peeling.unit?.name || _t(t("Unit"))} ({_t(t("Peeled"))})
                                        </span>
                                        <span
                                          className="badge"
                                          style={{
                                            backgroundColor: "#dcfce7",
                                            color: "#15803d",
                                            fontSize: "0.68rem",
                                            padding: "2px 6px",
                                          }}
                                        >
                                          {_t(t("Peeled"))}
                                        </span>
                                      </div>
                                      <div className="my-1">
                                        <span
                                          style={{
                                            fontSize: "1.2rem",
                                            fontWeight: "700",
                                            color: "#15803d",
                                            lineHeight: 1.2,
                                          }}
                                        >
                                          {currencySymbolLeft() || "Rs"}{" "}
                                          {calculations.costPerPeeled
                                            ? Number(calculations.costPerPeeled).toFixed(2)
                                            : "0.00"}{" "}
                                          {currencySymbolRight()}
                                        </span>
                                      </div>
                                      <div
                                        className="text-muted text-truncate"
                                        style={{ fontSize: "0.72rem" }}
                                      >
                                        {_t(t("vs raw"))} {currencySymbolLeft() || "Rs"}{" "}
                                        {peeling.cost_price
                                          ? Number(peeling.cost_price).toFixed(2)
                                          : "0.00"}{" "}
                                          {currencySymbolRight()}{" "}
                                        /{peeling.unit?.name || _t(t("unit"))}
                                      </div>
                                    </div>
                                  </div>

                                  {/* Raw Material Cost */}
                                  <div className="col-12 col-sm-6 col-lg-3 mb-2 mb-lg-0">
                                    <div
                                      className="rounded bg-white h-100 d-flex flex-column justify-content-between"
                                      style={{
                                        padding: "10px 14px",
                                        border: "1px solid #e2e8f0",
                                        borderRadius: "6px",
                                        minHeight: "72px",
                                      }}
                                    >
                                      <div className="d-flex justify-content-between align-items-center">
                                        <span
                                          className="text-uppercase font-weight-bold text-muted"
                                          style={{
                                            fontSize: "0.72rem",
                                            letterSpacing: "0.4px",
                                          }}
                                        >
                                          {_t(t("Raw Material Cost"))}
                                        </span>
                                      </div>
                                      <div className="my-1">
                                        <span
                                          style={{
                                            fontSize: "1.2rem",
                                            fontWeight: "700",
                                            color: "#1e293b",
                                            lineHeight: 1.2,
                                          }}
                                        >
                                          {currencySymbolLeft() || "Rs"}{" "}
                                          {Number(calculations.totalRawCost).toFixed(2)}{" "}
                                          {currencySymbolRight()}
                                        </span>
                                      </div>
                                      <div
                                        className="text-muted text-truncate"
                                        style={{ fontSize: "0.72rem" }}
                                      >
                                        {_t(t("Total Raw"))}: {peeling.before_peeling || 0}{" "}
                                        {peeling.unit?.name || ""}
                                      </div>
                                    </div>
                                  </div>

                                  {/* Wastage Cost */}
                                  <div className="col-12 col-sm-6 col-lg-3 mb-2 mb-sm-0">
                                    <div
                                      className="rounded h-100 d-flex flex-column justify-content-between"
                                      style={{
                                        padding: "10px 14px",
                                        backgroundColor:
                                          calculations.wastagePercent > 0 ? "#fff5f5" : "#ffffff",
                                        border: `1px solid ${
                                          calculations.wastagePercent > 0 ? "#fecaca" : "#e2e8f0"
                                        }`,
                                        borderRadius: "6px",
                                        minHeight: "72px",
                                      }}
                                    >
                                      <div className="d-flex justify-content-between align-items-center">
                                        <span
                                          className="text-uppercase font-weight-bold"
                                          style={{
                                            fontSize: "0.72rem",
                                            color:
                                              calculations.wastagePercent > 0 ? "#991b1b" : "#64748b",
                                            letterSpacing: "0.4px",
                                          }}
                                        >
                                          {_t(t("Wastage Cost"))}
                                        </span>
                                        <span
                                          className="badge"
                                          style={{
                                            backgroundColor:
                                              calculations.wastagePercent > 0 ? "#fee2e2" : "#f1f5f9",
                                            color:
                                              calculations.wastagePercent > 0 ? "#dc2626" : "#64748b",
                                            fontSize: "0.68rem",
                                            padding: "2px 6px",
                                          }}
                                        >
                                          {calculations.wastagePercent || 0}%
                                        </span>
                                      </div>
                                      <div className="my-1">
                                        <span
                                          style={{
                                            fontSize: "1.2rem",
                                            fontWeight: "700",
                                            color:
                                              calculations.wastagePercent > 0 ? "#dc2626" : "#64748b",
                                            lineHeight: 1.2,
                                          }}
                                        >
                                          {currencySymbolLeft() || "Rs"}{" "}
                                          {Number(calculations.wastageCost).toFixed(2)}{" "}
                                          {currencySymbolRight()}
                                        </span>
                                      </div>
                                      <div
                                        className="text-truncate"
                                        style={{
                                          fontSize: "0.72rem",
                                          color:
                                            calculations.wastagePercent > 0 ? "#b91c1c" : "#94a3b8",
                                        }}
                                      >
                                        {_t(t("Waste"))}: {calculations.wastage || 0}{" "}
                                        {peeling.unit?.name || ""}
                                      </div>
                                    </div>
                                  </div>

                                  {/* Yield */}
                                  <div className="col-12 col-sm-6 col-lg-3">
                                    <div
                                      className="rounded h-100 d-flex flex-column justify-content-between"
                                      style={{
                                        padding: "10px 14px",
                                        backgroundColor:
                                          calculations.yieldPercent > 0 ? "#f0fdf4" : "#ffffff",
                                        border: `1px solid ${
                                          calculations.yieldPercent > 0 ? "#bbf7d0" : "#e2e8f0"
                                        }`,
                                        borderRadius: "6px",
                                        minHeight: "72px",
                                      }}
                                    >
                                      <div className="d-flex justify-content-between align-items-center">
                                        <span
                                          className="text-uppercase font-weight-bold"
                                          style={{
                                            fontSize: "0.72rem",
                                            color:
                                              calculations.yieldPercent > 0 ? "#166534" : "#64748b",
                                            letterSpacing: "0.4px",
                                          }}
                                        >
                                          {_t(t("Yield Rate"))}
                                        </span>
                                        <span
                                          className="badge"
                                          style={{
                                            backgroundColor:
                                              calculations.yieldPercent > 0 ? "#dcfce7" : "#f1f5f9",
                                            color:
                                              calculations.yieldPercent > 0 ? "#15803d" : "#64748b",
                                            fontSize: "0.68rem",
                                            padding: "2px 6px",
                                          }}
                                        >
                                          {calculations.yieldPercent || 0}%
                                        </span>
                                      </div>
                                      <div className="my-1">
                                        <span
                                          style={{
                                            fontSize: "1.2rem",
                                            fontWeight: "700",
                                            color:
                                              calculations.yieldPercent > 0 ? "#15803d" : "#1e293b",
                                            lineHeight: 1.2,
                                          }}
                                        >
                                          {calculations.yieldPercent}%
                                        </span>
                                      </div>
                                      <div
                                        className="text-truncate"
                                        style={{
                                          fontSize: "0.72rem",
                                          color:
                                            calculations.yieldPercent > 0 ? "#166534" : "#94a3b8",
                                        }}
                                      >
                                        {_t(t("Net Peeled"))}: {peeling.after_peeling || 0}{" "}
                                        {peeling.unit?.name || ""}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Buttons matching PurchaseOrderAction */}
                            <div className="form-group mt-4 pb-2">
                              <div className="d-flex justify-content-end align-items-center">
                                <div className="col-12 d-flex justify-content-end">
                                  <button
                                    type="button"
                                    className="btn rounded hov-btn mr-3"
                                    onClick={() =>
                                      history.push(
                                        "/dashboard/manage/stock/peeling-process"
                                      )
                                    }
                                  >
                                    <i
                                      className="fa fa-arrow-left"
                                      aria-hidden="true"
                                    ></i>{" "}
                                    {_t(t("Back"))}
                                  </button>
                                  <button
                                    disabled={loading || submitting}
                                    type="submit"
                                    className="btn btn-primary rounded"
                                  >
                                    {submitting ? (
                                      <>
                                        <i className="fa fa-spinner fa-spin mr-1"></i>{" "}
                                        {_t(t("Saving..."))}
                                      </>
                                    ) : (
                                      <>
                                        <i
                                          className="fa fa-floppy-o mr-1"
                                          aria-hidden="true"
                                        ></i>{" "}
                                        {_t(t(isEdit ? "Update" : "Save"))}
                                      </>
                                    )}
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </form>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            {/* Rightbar contents end*/}
          </div>
        </div>
      </main>

      <style>{`
        .peeling-process-form .react-datepicker-wrapper,
        .peeling-process-form .react-datepicker__input-container {
          width: 100%;
          display: block;
        }
      `}</style>
    </>
  );
};

export default PeelingProcessAction;
